import type {
	AssignPalletContainerRequest,
	AssignPalletsRequest,
	AuditEntry,
	BulkDeleteRecordsRequest,
	BulkDeleteRecordsResponse,
	ClearAllRecordsRequest,
	ClearAllRecordsResponse,
	Container,
	CreateContainerRequest,
	CreateLocationRequest,
	CreateOperatorRequest,
	CreatePackingListRequest,
	CreatePackingRequestRequest,
	CreatePalletRequest,
	CreateRecordRequest,
	DispatchPackingListRequest,
	DispatchPackingListResponse,
	FulfillPackingRequestRequest,
	ListContainersResponse,
	ReorderContainersRequest,
	UpdateContainerRequest,
	ImportRecordsRequest,
	ImportRecordsResponse,
	ListAuditQuery,
	ListAuditResponse,
	ListPalletsQuery,
	ListPalletsResponse,
	ListRecordsQuery,
	ListRecordsResponse,
	LoadPipeRequest,
	Location,
	LoginRequest,
	LoginResponse,
	MeResponse,
	Operator,
	PackingList,
	PackingListSummary,
	PackingRequest,
	PackingRequestSummary,
	Pallet,
	Photo,
	PhotoScope,
	PhotoUploadResponse,
	PipeRecord,
	ReorderPalletsRequest,
	Role,
	ScanUpdateRequest,
	TakenPipesResponse,
	UpdatePackingRequestRequest,
	ScanUpdateResponse,
	SealPalletRequest,
	SetOperatorRolesRequest,
	UnloadPipeRequest,
	UnsealPalletRequest,
	UpdateOperatorRequest,
	UpdatePackingListRequest,
	UpdatePalletRequest,
	UpdateRecordRequest,
} from '../../../shared/contracts';

async function req<T>(url: string, init?: RequestInit): Promise<T> {
	const headers: Record<string, string> = {};
	const isFormData = init?.body instanceof FormData;
	if (!isFormData) headers['Content-Type'] = 'application/json';
	const res = await fetch(url, {
		credentials: 'same-origin',
		headers: { ...headers, ...((init?.headers as Record<string, string>) ?? {}) },
		...init,
	});
	if (!res.ok) {
		const body = await res.json().catch(() => ({ error: res.statusText }));
		throw Object.assign(new Error((body as { error?: string }).error ?? 'request_failed'), { status: res.status, body });
	}
	return (await res.json()) as T;
}

const json = (body: unknown) => ({ body: JSON.stringify(body) });

function buildQuery(params: Record<string, string | number | undefined | null>): string {
	const usp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v === undefined || v === null || v === '') continue;
		usp.set(k, String(v));
	}
	const s = usp.toString();
	return s ? `?${s}` : '';
}

export const api = {
	// auth
	me: () => req<MeResponse>('/api/auth/me'),
	login: (body: LoginRequest) => req<LoginResponse>('/api/auth/login', { method: 'POST', ...json(body) }),
	logout: () => req<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

	// pipes (formerly records)
	listRecords: (params: ListRecordsQuery = {}) =>
		req<ListRecordsResponse>(`/api/pipes${buildQuery(params as Record<string, string | number | undefined>)}`),
	listProjects: () => req<string[]>('/api/pipes/projects'),
	getRecord: (id: string) => req<PipeRecord>(`/api/pipes/${encodeURIComponent(id)}`),
	getRecordByQR: (qr: string) => req<PipeRecord>(`/api/pipes/by-qr/${encodeURIComponent(qr)}`),
	scanUpdateRecord: (body: ScanUpdateRequest) =>
		req<ScanUpdateResponse>('/api/pipes', { method: 'POST', ...json(body) }),
	createRecord: (body: CreateRecordRequest) =>
		req<PipeRecord>('/api/pipes/create', { method: 'POST', ...json(body) }),
	updateRecord: (id: string, body: UpdateRecordRequest) =>
		req<PipeRecord>(`/api/pipes/${id}`, { method: 'PATCH', ...json(body) }),
	deleteRecord: (id: string) => req<{ ok: true }>(`/api/pipes/${id}`, { method: 'DELETE' }),
	bulkDeleteRecords: (body: BulkDeleteRecordsRequest) =>
		req<BulkDeleteRecordsResponse>('/api/pipes/bulk-delete', { method: 'POST', ...json(body) }),
	clearAllRecords: (body: ClearAllRecordsRequest) =>
		req<ClearAllRecordsResponse>('/api/pipes', { method: 'DELETE', ...json(body) }),

	// projects catalog (server-side searchable typeahead)
	listProjectsCatalog: (q?: string, limit = 30) =>
		req<{ id: string; name: string; createdAt: string }[]>(`/api/projects${buildQuery({ q, limit })}`),
	listProjectsWithCounts: (q?: string, limit = 500) =>
		req<import('../../../shared/contracts').Project[]>(
			`/api/projects${buildQuery({ q, limit, with_counts: 1 })}`,
		),

	// locations
	listLocations: () => req<Location[]>('/api/locations'),
	createLocation: (body: CreateLocationRequest) =>
		req<Location>('/api/locations', { method: 'POST', ...json(body) }),
	deleteLocation: (id: string) => req<{ ok: true }>(`/api/locations/${id}`, { method: 'DELETE' }),

	// operators
	listOperators: () => req<Operator[]>('/api/operators'),
	createOperator: (body: CreateOperatorRequest) =>
		req<Operator>('/api/operators', { method: 'POST', ...json(body) }),
	updateOperator: (id: string, body: UpdateOperatorRequest) =>
		req<{ ok: true }>(`/api/operators/${id}`, { method: 'PATCH', ...json(body) }),
	setOperatorRoles: (id: string, body: SetOperatorRolesRequest) =>
		req<{ ok: true; roles: Role[] }>(`/api/operators/${id}/roles`, { method: 'PUT', ...json(body) }),
	deleteOperator: (id: string) => req<{ ok: true }>(`/api/operators/${id}`, { method: 'DELETE' }),

	// imports
	importRecords: (body: ImportRecordsRequest) =>
		req<ImportRecordsResponse>('/api/imports/records', { method: 'POST', ...json(body) }),

	// photos
	listPhotos: (scope: PhotoScope, scopeId: string) =>
		req<Photo[]>(`/api/photos${buildQuery({ scope, scope_id: scopeId })}`),
	uploadPhotos: (scope: PhotoScope, scopeId: string, files: File[], caption?: string) => {
		const fd = new FormData();
		fd.set('scope', scope);
		fd.set('scope_id', scopeId);
		if (caption) fd.set('caption', caption);
		for (const f of files) fd.append('files', f, f.name);
		return req<PhotoUploadResponse>('/api/photos', { method: 'POST', body: fd });
	},
	deletePhoto: (id: string) => req<{ ok: true }>(`/api/photos/${id}`, { method: 'DELETE' }),

	// audit
	listAudit: (params: ListAuditQuery) =>
		req<ListAuditResponse>(`/api/audit${buildQuery(params as Record<string, string | number | undefined>)}`),
	listAuditGlobal: (params: Omit<ListAuditQuery, 'target_id'> = {}) =>
		req<ListAuditResponse>(`/api/audit/global${buildQuery(params as Record<string, string | number | undefined>)}`),

	// pallets
	listPallets: (params: ListPalletsQuery = {}) =>
		req<ListPalletsResponse>(`/api/pallets${buildQuery(params as Record<string, string | number | undefined>)}`),
	getPallet: (id: string) => req<Pallet>(`/api/pallets/${encodeURIComponent(id)}`),
	createPallet: (body: CreatePalletRequest) =>
		req<Pallet>('/api/pallets', { method: 'POST', ...json(body) }),
	updatePallet: (id: string, body: UpdatePalletRequest) =>
		req<Pallet>(`/api/pallets/${id}`, { method: 'PATCH', ...json(body) }),
	sealPallet: (id: string, body: SealPalletRequest = {}) =>
		req<Pallet>(`/api/pallets/${id}/seal`, { method: 'POST', ...json(body) }),
	unsealPallet: (id: string, body: UnsealPalletRequest = {}) =>
		req<Pallet>(`/api/pallets/${id}/unseal`, { method: 'POST', ...json(body) }),
	loadPipeOnPallet: (id: string, body: LoadPipeRequest) =>
		req<Pallet>(`/api/pallets/${id}/load`, { method: 'POST', ...json(body) }),
	unloadPipeFromPallet: (id: string, body: UnloadPipeRequest) =>
		req<Pallet>(`/api/pallets/${id}/unload`, { method: 'POST', ...json(body) }),
	loadPalletIntoContainer: (id: string, body: AssignPalletContainerRequest) =>
		req<Pallet>(`/api/pallets/${id}/load-container`, { method: 'POST', ...json(body) }),
	unloadPalletFromContainer: (id: string) =>
		req<Pallet>(`/api/pallets/${id}/unload-container`, { method: 'POST' }),
	deletePallet: (id: string) => req<{ ok: true }>(`/api/pallets/${id}`, { method: 'DELETE' }),

	// containers
	listContainers: (packingListId: string) =>
		req<ListContainersResponse>(`/api/containers${buildQuery({ packing_list_id: packingListId })}`),
	createContainer: (body: CreateContainerRequest) =>
		req<Container>('/api/containers', { method: 'POST', ...json(body) }),
	updateContainer: (id: string, body: UpdateContainerRequest) =>
		req<Container>(`/api/containers/${id}`, { method: 'PATCH', ...json(body) }),
	reorderContainers: (body: ReorderContainersRequest) =>
		req<ListContainersResponse>('/api/containers/reorder', { method: 'POST', ...json(body) }),
	deleteContainer: (id: string) => req<{ ok: true }>(`/api/containers/${id}`, { method: 'DELETE' }),

	// packing lists
	listPackingLists: (params: { status?: string; q?: string; project?: string; limit?: number } = {}) =>
		req<{ items: PackingListSummary[] }>(`/api/packing-lists${buildQuery(params as Record<string, string | number | undefined>)}`),
	getPackingList: (id: string) => req<PackingList>(`/api/packing-lists/${encodeURIComponent(id)}`),
	createPackingList: (body: CreatePackingListRequest) =>
		req<PackingList>('/api/packing-lists', { method: 'POST', ...json(body) }),
	updatePackingList: (id: string, body: UpdatePackingListRequest) =>
		req<PackingList>(`/api/packing-lists/${id}`, { method: 'PATCH', ...json(body) }),
	assignPallets: (id: string, body: AssignPalletsRequest) =>
		req<PackingList>(`/api/packing-lists/${id}/pallets`, { method: 'POST', ...json(body) }),
	unassignPallet: (id: string, palletId: string) =>
		req<PackingList>(`/api/packing-lists/${id}/pallets/${palletId}`, { method: 'DELETE' }),
	reorderPallets: (id: string, body: ReorderPalletsRequest) =>
		req<PackingList>(`/api/packing-lists/${id}/reorder`, { method: 'POST', ...json(body) }),
	dispatchPackingList: (id: string, body: DispatchPackingListRequest = {}) =>
		req<DispatchPackingListResponse>(`/api/packing-lists/${id}/dispatch`, { method: 'POST', ...json(body) }),
	reopenPackingList: (id: string) =>
		req<PackingList>(`/api/packing-lists/${id}/reopen`, { method: 'POST' }),
	closePackingList: (id: string) =>
		req<PackingList>(`/api/packing-lists/${id}/close`, { method: 'POST' }),
	deletePackingList: (id: string) =>
		req<{ ok: true }>(`/api/packing-lists/${id}`, { method: 'DELETE' }),
	packingListManifestUrl: (id: string) => `/manifest/${encodeURIComponent(id)}`,

	// packing requests (project-manager driven)
	listPackingRequests: (params: { status?: string; q?: string; project?: string; limit?: number } = {}) =>
		req<{ items: PackingRequestSummary[] }>(`/api/packing-requests${buildQuery(params as Record<string, string | number | undefined>)}`),
	getPackingRequest: (id: string) => req<PackingRequest>(`/api/packing-requests/${encodeURIComponent(id)}`),
	createPackingRequest: (body: CreatePackingRequestRequest) =>
		req<PackingRequest>('/api/packing-requests', { method: 'POST', ...json(body) }),
	updatePackingRequest: (id: string, body: UpdatePackingRequestRequest) =>
		req<PackingRequest>(`/api/packing-requests/${id}`, { method: 'PATCH', ...json(body) }),
	fulfillPackingRequest: (id: string, body: FulfillPackingRequestRequest = {}) =>
		req<PackingRequest>(`/api/packing-requests/${id}/fulfill`, { method: 'POST', ...json(body) }),
	cancelPackingRequest: (id: string) =>
		req<PackingRequest>(`/api/packing-requests/${id}/cancel`, { method: 'POST' }),
	deletePackingRequest: (id: string) =>
		req<{ ok: true }>(`/api/packing-requests/${id}`, { method: 'DELETE' }),
	takenPipes: (project?: string) =>
		req<TakenPipesResponse>(`/api/packing-requests/taken-pipes${buildQuery({ project })}`),
};

export type ApiAudit = AuditEntry;
